import pipe from 'ramda/es/pipe'
import values from 'ramda/es/values'
import map from 'ramda/es/map'
import isEmpty from 'ramda/es/isEmpty'
import sum from 'ramda/es/sum'
import prop from 'ramda/es/prop'
import sortWith from 'ramda/es/sortWith'
import lensPath from 'ramda/es/lensPath'
import ascend from 'ramda/es/ascend'
import descend from 'ramda/es/descend'
import view from 'ramda/es/view'

import type { ComplexItem, DishItem } from 'app/common/types/food/schools/complexes'
import type { ComplexLastChange } from 'app/common/types/food/schools/complexes/history'
import toFixedValue from 'app/common/lib/toFixedValue'

export type Dish = {
  id: number
  title: string
  portion: string
  cost: string
  index: number
}

const mapDishes: (items: { [id: string]: DishItem }) => Dish[] = pipe(
  values,
  map(({ id, title, portion, cost, index }: DishItem) => ({
    id,
    title: (title || '').trim(),
    portion: portion || '',
    cost: toFixedValue(cost),
    index,
  })),
  sortWith([ascend(prop('index')), ascend(prop('title'))]),
)

const calcCost = (
  isFixed: boolean,
  fixedPrice?: string,
  items?: { [id: string]: DishItem },
): string => {
  if (isFixed && fixedPrice !== undefined && fixedPrice !== null) {
    return toFixedValue(fixedPrice)
  }
  if (items && !isEmpty(items)) {
    return toFixedValue(
      pipe(values, (list: DishItem[]) => list.map((item) => item.cost), sum)(items),
    )
  }
  return '—'
}

export type Complex = {
  key: number
  id: number
  newItem: boolean
  empty: boolean
  deletable: boolean
  position: number //порядок сортировки
  subsidyOrders: boolean
  photo: string | null
  lastChange?: ComplexLastChange
  params: {
    id: number
    title: string
    dishes: Array<Dish>
    cost: string
    grocerySetPrice: string | null
    fixedPriceEnabled: boolean
    buffetZone: boolean
    hotMealsZone: boolean
    denyParentMoneySales: boolean
    denyMoreThanOneSales: boolean
    disableForTerminalGuestSales: boolean
    useAsAdditional: boolean
    fundMovementTypeCode?: number | null
  }
}

type ComplexesInItem = {
  isNew?: boolean
  // ...$Exact<ComplexItem>
} & ComplexItem

export type ComplexesIn = {
  [id: string]: ComplexesInItem
}

export const Kits2ExtendedItems: (ComplexesIn: ComplexesIn) => Array<Complex> = pipe(
  values,
  map(
    ({
      id,
      title,
      fixed_price_enabled: fpEnabled,
      fixed_price: fp,
      grocery_set_price: grocerySetPrice,
      isNew,
      items,
      deletable,
      position,
      subsidy_orders: subsidyOrders,
      photo_url: photo,
      buffet_zone: buffetZone,
      hot_meals_zone: hotMealsZone,
      deny_parent_money_sales: denyParentMoneySales,
      deny_more_than_one_sales: denyMoreThanOneSales,
      disable_for_terminal_guest_sales: disableForTerminalGuestSales,
      use_as_additional: useAsAdditional,
      fund_movement_type_code: fundMovementTypeCode,
      last_change: lastChange,
    }: ComplexesInItem): Complex => ({
      key: id,
      id,
      newItem: Boolean(isNew),
      empty: isEmpty(items || ''),
      deletable,
      subsidyOrders: Boolean(subsidyOrders && !isEmpty(subsidyOrders)),
      photo,
      position,
      lastChange,
      params: {
        id,
        title: (title || '').trim(),
        dishes: items && !isEmpty(items) ? mapDishes(items) : [],
        cost: calcCost(fpEnabled, fp, items),
        fixedPriceEnabled: fpEnabled,
        grocerySetPrice: toFixedValue(grocerySetPrice),
        buffetZone,
        hotMealsZone,
        denyParentMoneySales,
        denyMoreThanOneSales,
        disableForTerminalGuestSales,
        useAsAdditional,
        fundMovementTypeCode,
      },
    }),
  ),
  sortWith([
    descend(prop('newItem')),
    ascend(prop('empty')),
    ascend(prop('position')),
    ascend(view(lensPath(['params', 'title']))),
  ]),
)
